import Amplitude from 'amplitude-js';
import axios, { AxiosRequestConfig } from 'axios';
import { DateTime } from 'luxon';
import qs from 'qs';

import ApiClient, { ApiOptions } from 'lib/clients/ApiClient';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import { Iso8601DateString } from 'lib/types';

export const API_ROOT_DASHBOARD = '/api/internal/dashboard/v1';

export class Client extends ApiClient {
  public expiresAt?: string;
  public isFetchingSessionToken: boolean;

  private myAccessToken?: string;
  // Used to set Persona-Country header to route requests to our servers in that country
  private countryToRouteRequests?: string;

  constructor(search: string) {
    super(API_ROOT_DASHBOARD);

    this.isFetchingSessionToken = false;

    // NB: saml_controller passes new refresh tokens through a GET parameter
    // unless we use the new saml-user-session-access-token parameter, which
    // gets exchanged for the refresh token.
    const parsedParams = qs.parse(search, { ignoreQueryPrefix: true });
    if (
      parsedParams['session-refresh-token'] &&
      typeof parsedParams['session-refresh-token'] === 'string'
    ) {
      this.setTokens(parsedParams['session-refresh-token']);
    } else if (parsedParams['refresh-token'] && typeof parsedParams['refresh-token'] === 'string') {
      this.setTokens(parsedParams['refresh-token']);
    } else if (
      parsedParams['saml-user-session-access-token'] &&
      typeof parsedParams['saml-user-session-access-token'] === 'string'
    ) {
      this.fetchAndSetSessionToken(parsedParams['saml-user-session-access-token']);
    }
  }

  public get refreshToken() {
    return StorageClient.getLocalItem(LocalStorageKey.DashboardRefreshTokenSession);
  }

  public get accessToken() {
    return this.myAccessToken;
  }

  public set personaCountry(country: string) {
    this.countryToRouteRequests = country;
  }

  // Session management
  public setTokens(refreshToken: string, accessToken?: string) {
    if (refreshToken) {
      StorageClient.setLocalItem(LocalStorageKey.DashboardRefreshTokenSession, refreshToken);
    }
    this.myAccessToken = accessToken;
  }

  public clearTokens() {
    this.myAccessToken = undefined;

    StorageClient.removeLocalItem(LocalStorageKey.DashboardCurrentEnvironmentId);
    StorageClient.removeLocalItem(LocalStorageKey.DashboardRefreshTokenSession);
    StorageClient.removeLocalItem(LocalStorageKey.DashboardActivityAt);
    // IPStack is deprecated but may still exist in local storage for some users
    StorageClient.removeLocalItem(LocalStorageKey.IpStackCacheKey);
    StorageClient.removeLocalItem(LocalStorageKey.LocaleInfoCacheKey);
    StorageClient.removeLocalItem(LocalStorageKey.UserTraits);
    StorageClient.removeLocalItem(LocalStorageKey.GroupProperties);

    Amplitude.getInstance().clearUserProperties();
  }

  // Request handling
  public isLoggedIn() {
    return !!this.refreshToken && !!this.accessToken;
  }

  public hasExpired() {
    const current = DateTime.local();
    return DateTime.fromISO(this.expiresAt as Iso8601DateString) <= current;
  }

  public getAuthHeaders() {
    if (this.accessToken) {
      return { Authorization: `Bearer ${this.accessToken}` };
    }
  }

  public handleExpiration() {
    document.location.reload();
  }

  protected async sendRequest(
    uri: string,
    requestOptions: AxiosRequestConfig,
    options: ApiOptions = {},
  ) {
    const environmentId = StorageClient.getLocalItem(LocalStorageKey.DashboardCurrentEnvironmentId);

    const requestOptionsWithHeaders = {
      ...requestOptions,
      headers: {
        ...(environmentId ? { 'Persona-Environment-ID': environmentId } : {}),
        ...requestOptions.headers,
        ...this.getAuthHeaders(),
        ...(this.countryToRouteRequests ? { 'Persona-Country': this.countryToRouteRequests } : {}),
      },
    };

    if (options.ignoreAccessToken || !this.hasExpired()) {
      return super.sendRequest(uri, requestOptionsWithHeaders, options);
    } else {
      // We do NOT want to resolve when handling expired requests
      this.handleExpiration();
      // This should get handled by useApiLoader, etc. and early exit,
      // preventing errors where the request response is empty.
      return Promise.reject();
    }
  }

  private fetchAndSetSessionToken(samlUserSessionAccessToken: string) {
    this.isFetchingSessionToken = true;

    axios
      .post('/saml/fetch_session_refresh_token', {
        saml_user_session_access_token: samlUserSessionAccessToken,
      })
      .then((response) => {
        this.setTokens(response.data['session-refresh-token']);

        const url = new URL(window.location.href);
        url.searchParams.delete('saml-user-session-access-token');
        window.location.href = url.toString();
      });
  }
}

export default new Client(window.location.search);
